import { SAMPLE_PLATE_LETTERS, SAMPLE_PLATE_NUMBERS } from '../../../../constants/saxs'
import {
  getPlateGroupsExperiment,
  getPlatesByPlateGroupId,
  getSamples,
  mapBufferColorsToSpecimenIdByExperiment,
  mapMacromoleculeColorsToMacromoleculeIdByExperiment,
} from '../../../../helpers/Saxs/Experiment'

import Plate from './Plate/plate'
import PropTypes from 'prop-types'
import React from 'react'
import find from 'lodash/find'

/** Local constants */
const ROUNDED_WELL = 'round'
const SQUARED_WELL = 'square'

/**
 * React component which renders a set of plates
 */
class SaxsPlatesPanel extends React.Component {
  constructor(props) {
    super(props)
    this.getFilledWellsByPlate = this.getFilledWellsByPlate.bind(this)
    this.getDimensions = this.getDimensions.bind(this)
    this.getPlates = this.getPlates.bind(this)
    this.getPlateBySlotPosition = this.getPlateBySlotPosition.bind(this)
    this.getWellShape = this.getWellShape.bind(this)
    this.getWellSize = this.getWellSize.bind(this)
  }

  render() {
    if (!this.props.experiment) {
      return null
    }
    return this.getPlates()
  }

  /**
   * Get all the plates of the experiment to be rendered sorted by deck
   * @returns {array} plates
   */
  getPlates() {
    let result = []
    let bufferColorMapper = mapBufferColorsToSpecimenIdByExperiment(this.props.experiment)
    let macromoleculeColorMapper = mapMacromoleculeColorsToMacromoleculeIdByExperiment(this.props.experiment)

    let plateGroups = getPlateGroupsExperiment(this.props.experiment)

    for (let i = 0; i < plateGroups.length; i++) {
      let id = plateGroups[i].plateGroupId
      let plates = getPlatesByPlateGroupId(this.props.experiment, id)
      let deckDimentions = this.getDimensions(plates)

      for (let j = 1; j <= deckDimentions.maxSlotPositionRow; j++) {
        for (let k = 1; k <= deckDimentions.maxSlotPositionColumn; k++) {
          let plate = this.getPlateBySlotPosition(plates, j, k)
          let rowLabels =
            plate.plateType && plate.plateType.rowCount > 0
              ? SAMPLE_PLATE_LETTERS.slice(0, plate.plateType.rowCount)
              : null
          let colLabels =
            plate.plateType && plate.plateType.columnCount > 0
              ? SAMPLE_PLATE_NUMBERS.slice(0, plate.plateType.columnCount)
              : null
          if (rowLabels && colLabels) {
            result.push(
              <div key={plate.name} style={{ borderWidth: '1px', borderStyle: 'solid', marginBottom: '10px' }}>
                <div style={{ paddingLeft: '10px' }}> {plate && plate.plateType ? plate.plateType.name : null} </div>
                <Plate
                  colLabels={colLabels}
                  filledWells={this.getFilledWellsByPlate(
                    this.props.experiment,
                    plate,
                    bufferColorMapper,
                    macromoleculeColorMapper
                  )}
                  rowLabels={rowLabels}
                  wellShape={this.getWellShape(plate)}
                  wellSize={this.getWellSize(plate)}
                />
              </div>
            )
          }
        }
      }
    }
    return result
  }

  /**
   * Get the shape of the wells for a given plate
   * @param {*} plate the plate
   * @returns {String}  well shape: 'round' or 'square'
   */
  getWellShape(plate) {
    if (plate && plate.plateType) {
      if (plate.plateType.name && plate.plateType.name === ' 4 x ( 8 + 3 ) Block') {
        return ROUNDED_WELL
      } else if (plate.plateType.name && plate.plateType.name === 'Deep Well') {
        return SQUARED_WELL
      } else if (plate.plateType.name && plate.plateType.name === '96 Well plate') {
        return ROUNDED_WELL
      }
    }
  }

  /**
   * Get the well size for a given plate
   * @param {*} plate the plate
   * @returns {Array} list of well size in percentage. Null if not avaialble
   */
  getWellSize(plate) {
    if (plate && plate.plateType && plate.plateType.name === ' 4 x ( 8 + 3 ) Block') {
      return [50, 50, 50, 50, 50, 50, 50, 50, 80, 80, 80]
    }
    return null
  }

  /**
   * Get filled well list for a given plate
   * @param {*} experiment experiment
   * @param {Object} plate plate of interest
   * @param {Object} bufferColorMapper the mapper fix associate a color to the specimenId for all buffers of the experiment.
   * @param {Object} macromoleculeColorMapper the mapper fix associate a color to the macromoleculeId for all macromolecules of the experiment.
   * @return {array} a list of wells inn the cuurent format supported by the plate component. Example: [{plate: 'plateName', row: 'A', column: 2}]
   */
  getFilledWellsByPlate(experiment, plate, bufferColorMapper, macromoleculeColorMapper) {
    let experimentSpecimens = getSamples(experiment)

    let filledWells = []
    if (experimentSpecimens && experimentSpecimens instanceof Array && experimentSpecimens.length > 0) {
      experimentSpecimens.forEach(specimen => {
        if (specimen.samplePlatePosition) {
          if (specimen.samplePlatePosition.samplePlateId === plate.samplePlateId) {
            // this sample is part of the plate of interest
            if (!specimen.macromolecule) {
              // this is a buffer
              filledWells.push({
                plate: plate.name,
                row: SAMPLE_PLATE_LETTERS[specimen.samplePlatePosition.rowNumber - 1],
                col: specimen.samplePlatePosition.columnNumber,
                color: bufferColorMapper[specimen.specimenId],
                strokeColor: 'blue',
              })
            } else {
              // this is a macromolecule
              filledWells.push({
                plate: plate.name,
                row: SAMPLE_PLATE_LETTERS[specimen.samplePlatePosition.rowNumber - 1],
                col: specimen.samplePlatePosition.columnNumber,
                color: macromoleculeColorMapper[specimen.macromolecule.macromoleculeId],
                strokeColor: 'black',
              })
            }
          }
        }
      })
    }
    return filledWells
  }

  /**
   * Get Deck dimension for the plates of a given plate group
   * @return {Object} Number of plates in a row on the deck and number of plate in a column on the deck
   */
  getDimensions = function (plates) {
    let maxSlotPositionRow = 0
    let maxSlotPositionColumn = 0

    if (plates !== null) {
      for (let i = 0; i < plates.length; i++) {
        /** Row **/
        let slotPositionRow = plates[i].slotPositionRow
        if (slotPositionRow !== null) {
          slotPositionRow = parseFloat(slotPositionRow)
          if (slotPositionRow > maxSlotPositionRow) {
            maxSlotPositionRow = slotPositionRow
          }
        }
        /** Column **/
        let slotPositionColumn = plates[i].slotPositionColumn
        if (slotPositionColumn !== null) {
          slotPositionColumn = parseFloat(slotPositionColumn)
          if (slotPositionColumn > maxSlotPositionColumn) {
            maxSlotPositionColumn = slotPositionColumn
          }
        }
      }
    }
    return {
      maxSlotPositionRow: parseFloat(maxSlotPositionRow),
      maxSlotPositionColumn: parseFloat(maxSlotPositionColumn),
    }
  }

  /**
   * This returns a sample plate object based on the position it occupies on the deck
   * @param {array} plates all plates of the deck
   * @param {row} row deck row
   * @param {col} column deck column
   * @returns {Object} plate found on the speficied deck position. Null when not found
   */
  getPlateBySlotPosition = function (plates, row, column) {
    if (plates && plates instanceof Array) {
      return (
        find(plates, plate => plate.slotPositionRow === String(row) && plate.slotPositionColumn === String(column)) ||
        null
      )
    }
    return null
  }
}

export default SaxsPlatesPanel

SaxsPlatesPanel.propTypes = {
  /* Experiment object*/
  experiment: PropTypes.object,
}
